import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tabs, Tag, Button, Input, Select, Popconfirm, Alert,
  Row, Col, Statistic, Space, Typography, Empty, Checkbox, Progress, theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  SafetyCertificateOutlined, ExclamationCircleOutlined, ClockCircleOutlined,
  CheckCircleOutlined, CheckCircleFilled, CloseCircleOutlined, PlusOutlined,
  DeleteOutlined, CheckOutlined, CloseOutlined, PrinterOutlined, SyncOutlined,
  LeftOutlined, RightOutlined, CalendarOutlined, UnorderedListOutlined,
  WarningOutlined, FileAddOutlined,
} from '@ant-design/icons';
import { ComplianceRecord, FacilityAsset, SystemKey } from '../../models/types';
import {
  createComplianceRecord, getComplianceRecords,
  updateComplianceRecord, deleteComplianceRecord,
} from '../../firebase/compliance';
import { getPMSchedules, createWorkOrder } from '../../firebase/pmScheduler';
import { getFacilityAssessments } from '../../firebase/firestore';
import { scoreAsset } from '../facility/facilityCalculations';
// PM_TASK_LIBRARY removed — not used in this module

interface Props { projectId: string; projectName?: string; orgName?: string; }

type View = 'calendar' | 'register' | 'critical';

// Defined at module level (not inside component) to avoid re-creation on every render
interface CriticalAsset extends FacilityAsset {
  system: SystemKey;
  assessmentId: string;
  assessmentName?: string;
  computedScore: number;
}

const MONTH_NAMES = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];
const DOW_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

// Common compliance standards grouped for quick-add
const COMPLIANCE_TEMPLATES = [
  { standard: 'NFPA 10', taskName: 'Fire Extinguisher Annual Service', frequencyValue: 1, frequencyUnit: 'years' as const, assetDescription: 'Fire Extinguishers' },
  { standard: 'NFPA 25', taskName: 'Sprinkler System Quarterly Inspection', frequencyValue: 3, frequencyUnit: 'months' as const, assetDescription: 'Fire Sprinkler System' },
  { standard: 'NFPA 25', taskName: 'Sprinkler System Annual Flow Test', frequencyValue: 1, frequencyUnit: 'years' as const, assetDescription: 'Fire Sprinkler System' },
  { standard: 'NFPA 72', taskName: 'Fire Alarm Annual Inspection & Test', frequencyValue: 1, frequencyUnit: 'years' as const, assetDescription: 'Fire Alarm System' },
  { standard: 'NFPA 101', taskName: 'Emergency Lighting 90-Min Full Test', frequencyValue: 1, frequencyUnit: 'years' as const, assetDescription: 'Emergency Lighting' },
  { standard: 'NFPA 110', taskName: 'Generator Annual Load Bank Test', frequencyValue: 1, frequencyUnit: 'years' as const, assetDescription: 'Emergency Generator' },
  { standard: 'NFPA 110', taskName: 'Generator Monthly Run Test', frequencyValue: 1, frequencyUnit: 'months' as const, assetDescription: 'Emergency Generator' },
  { standard: 'AWWA / Local Code', taskName: 'Backflow Preventer Annual Test & Certification', frequencyValue: 1, frequencyUnit: 'years' as const, assetDescription: 'Backflow Preventer' },
  { standard: 'ASHRAE 188', taskName: 'Cooling Tower Cleaning & Legionella Prevention', frequencyValue: 6, frequencyUnit: 'months' as const, assetDescription: 'Cooling Tower' },
  { standard: 'ASME / Local Boiler Code', taskName: 'Boiler Annual Inspection', frequencyValue: 1, frequencyUnit: 'years' as const, assetDescription: 'Boiler' },
  { standard: 'Local Code', taskName: 'Elevator Annual Inspection', frequencyValue: 1, frequencyUnit: 'years' as const, assetDescription: 'Elevator' },
  { standard: 'NFPA 96', taskName: 'Kitchen Hood Suppression Semi-Annual Inspection', frequencyValue: 6, frequencyUnit: 'months' as const, assetDescription: 'Kitchen Hood System' },
];

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

function parseLocalDate(s: string): Date {
  const [y,m,d] = s.split('-').map(Number);
  return new Date(y, m-1, d, 12);
}

function addFrequency(from: string, value: number, unit: ComplianceRecord['frequencyUnit']): string {
  const d = parseLocalDate(from);
  if (unit === 'days') d.setDate(d.getDate() + value);
  else if (unit === 'weeks') d.setDate(d.getDate() + value * 7);
  else if (unit === 'months') d.setMonth(d.getMonth() + value);
  else if (unit === 'years') d.setFullYear(d.getFullYear() + value);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function buildGrid(year: number, month: number): (string|null)[] {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const grid: (string|null)[] = [];
  for (let i = 0; i < firstDay; i++) grid.push(null);
  for (let d = 1; d <= daysInMonth; d++)
    grid.push(`${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`);
  while (grid.length % 7 !== 0) grid.push(null);
  return grid;
}

/* AntD preset tag colors (was bootstrap danger/warning/success) */
const STATUS_COLOR: Record<ComplianceRecord['status'], string> = {
  overdue: 'red',
  'due-soon': 'gold',
  current: 'green',
};
const STATUS_LABEL: Record<ComplianceRecord['status'], string> = {
  overdue: 'Overdue',
  'due-soon': 'Due Soon',
  current: 'Current',
};
/* Matches FacilityModule's rating tag palette */
const RATING_TAG: Record<string, string> = { Failed: 'red', Critical: 'volcano', Poor: 'gold' };

const emptyForm = () => ({
  assetDescription: '', assetLocation: '', standard: '', taskName: '',
  frequencyValue: 1, frequencyUnit: 'years' as ComplianceRecord['frequencyUnit'],
  nextDueDate: today(), lastCompletedDate: '', certificateFiled: false,
  certificateNumber: '', inspector: '', notes: '', templateIndex: '',
});

const ComplianceCalendar: React.FC<Props> = ({ projectId, projectName, orgName }) => {
  const { token } = theme.useToken();
  const [records, setRecords] = useState<ComplianceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('calendar');

  const [criticalAssets, setCriticalAssets] = useState<CriticalAsset[]>([]);
  const [creatingWOFor, setCreatingWOFor] = useState<string | null>(null);

  const [calDate, setCalDate] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [completingId, setCompletingId] = useState<string | null>(null);
  const [completeForm, setCompleteForm] = useState({ completedDate: today(), certificateFiled: false, certificateNumber: '', inspector: '' });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [recs, assessments] = await Promise.all([
        getComplianceRecords(projectId),
        getFacilityAssessments(projectId),
      ]);
      setRecords(recs);

      // Extract Failed/Critical/Poor assets from all assessments
      const CRITICAL_RATINGS = new Set(['Failed', 'Critical', 'Poor']);
      const SYSTEMS: SystemKey[] = ['controls', 'electrical', 'mechanical', 'plumbing'];
      const critical: CriticalAsset[] = [];
      for (const assessment of assessments) {
        for (const system of SYSTEMS) {
          const sysData = assessment[system];
          if (!sysData?.assets) continue;
          for (const asset of sysData.assets) {
            // Unassessed assets have no rating — scoreAsset returns {} — so they are never flagged
            // critical off their defaults.
            const { score, rating } = scoreAsset(asset);
            if (rating && CRITICAL_RATINGS.has(rating)) {
              critical.push({
                ...asset,
                system,
                assessmentId: assessment.id ?? '',
                assessmentName: assessment.buildingName,
                computedScore: score ?? 0,
                rating,
              });
            }
          }
        }
      }
      // Sort: Failed first, then Critical, then Poor
      const order: Record<string, number> = { Failed: 0, Critical: 1, Poor: 2 };
      critical.sort((a, b) => (order[a.rating ?? ''] ?? 3) - (order[b.rating ?? ''] ?? 3));
      setCriticalAssets(critical);
    }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // ── Auto-sync from PM Schedules ───────────────────────────────────────
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const syncFromPMSchedules = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const schedules = await getPMSchedules(projectId);
      const complianceSchedules = schedules.filter(s => s.complianceRequired && s.complianceStandard && s.status === 'active');
      const existing = await getComplianceRecords(projectId);
      const existingScheduleIds = new Set(existing.map(r => r.scheduleId).filter(Boolean));

      let added = 0;
      for (const s of complianceSchedules) {
        if (existingScheduleIds.has(s.id)) continue; // already synced
        await createComplianceRecord({
          projectId,
          scheduleId: s.id,
          assetDescription: s.assetDescription,
          assetLocation: s.assetLocation || undefined,
          standard: s.complianceStandard!,
          taskName: s.taskName,
          frequencyValue: s.frequencyValue,
          frequencyUnit: s.frequencyUnit,
          nextDueDate: s.nextDueDate,
          lastCompletedDate: s.lastCompletedDate || undefined,
          certificateFiled: false,
        });
        added++;
      }
      setSyncResult(added > 0 ? `✅ Synced ${added} new compliance item${added > 1 ? 's' : ''} from PM Schedules.` : 'No new compliance items to sync — all PM schedules already have compliance records.');
      await load();
    } catch (e: any) { setError(e.message); }
    finally { setSyncing(false); }
  };

  // ── Print compliance report ────────────────────────────────────────────
  const printReport = () => {
    const scoreColor = healthScore >= 80 ? '#198754' : healthScore >= 50 ? '#fd7e14' : '#dc3545';
    const genDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const buildingName = projectName || projectId;

    // Group records by status for summary
    const overdueRows = records.filter(r => r.status === 'overdue');
    const dueSoonRows = records.filter(r => r.status === 'due-soon');
    const currentRows = records.filter(r => r.status === 'current');

    const renderRows = (items: ComplianceRecord[], rowBg: string) => items.map(r => `
      <tr style="background:${rowBg};">
        <td style="padding:7px 10px;font-size:11px;"><strong>${r.standard}</strong></td>
        <td style="padding:7px 10px;font-size:11px;">${r.taskName}</td>
        <td style="padding:7px 10px;font-size:11px;">${r.assetDescription}${r.assetLocation ? ' &middot; ' + r.assetLocation : ''}</td>
        <td style="padding:7px 10px;font-size:11px;text-align:center;">${r.frequencyValue} ${r.frequencyUnit}</td>
        <td style="padding:7px 10px;font-size:11px;">${r.lastCompletedDate || '&mdash;'}</td>
        <td style="padding:7px 10px;font-size:11px;font-weight:600;color:${r.status === 'overdue' ? '#dc3545' : r.status === 'due-soon' ? '#b45309' : '#198754'}">${r.nextDueDate}</td>
        <td style="padding:7px 10px;font-size:11px;text-align:center;">${r.certificateFiled ? '✓' : ''}</td>
        <td style="padding:7px 10px;font-size:11px;">${r.certificateNumber || ''}</td>
        <td style="padding:7px 10px;font-size:11px;">${r.inspector || ''}</td>
      </tr>`).join('');

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html>
<html lang="en"><head>
<meta charset="UTF-8">
<title>Compliance Report — ${buildingName}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #212529; background: white; }
  .page { max-width: 1100px; margin: 0 auto; padding: 40px 48px; }
  /* Header */
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #212529; padding-bottom: 16px; margin-bottom: 24px; }
  .header-left h1 { font-size: 22px; font-weight: 700; }
  .header-left .subtitle { font-size: 13px; color: #6c757d; margin-top: 4px; }
  .header-right { text-align: right; }
  .header-right .org { font-size: 13px; font-weight: 600; }
  .header-right .gen-date { font-size: 11px; color: #6c757d; margin-top: 2px; }
  /* Score cards */
  .score-row { display: flex; gap: 16px; margin-bottom: 28px; }
  .score-card { flex: 1; border: 1px solid #dee2e6; border-radius: 8px; padding: 14px 18px; text-align: center; }
  .score-card .val { font-size: 26px; font-weight: 700; }
  .score-card .lbl { font-size: 11px; color: #6c757d; margin-top: 2px; }
  /* Section header */
  .section-head { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; padding: 8px 10px; margin-bottom: 0; }
  .section-overdue { background: #f8d7da; color: #842029; }
  .section-duesoon { background: #fff3cd; color: #664d03; }
  .section-current { background: #d1e7dd; color: #0a3622; }
  /* Table */
  table { width: 100%; border-collapse: collapse; margin-bottom: 24px; font-size: 11px; }
  th { background: #343a40; color: white; padding: 7px 10px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; }
  td { border-bottom: 1px solid #e9ecef; vertical-align: top; }
  /* Footer */
  .footer { margin-top: 32px; border-top: 1px solid #dee2e6; padding-top: 12px; font-size: 10px; color: #adb5bd; display: flex; justify-content: space-between; }
  @media print {
    .page { padding: 20px; }
    @page { margin: 1.5cm; }
  }
</style>
</head><body><div class="page">

  <div class="header">
    <div class="header-left">
      <h1>Compliance Register</h1>
      <div class="subtitle">${buildingName}</div>
    </div>
    <div class="header-right">
      <div class="org">${orgName || 'MEP Tools'}</div>
      <div class="gen-date">Generated: ${genDate}</div>
    </div>
  </div>

  <div class="score-row">
    <div class="score-card" style="border-color:${scoreColor};">
      <div class="val" style="color:${scoreColor}">${healthScore}%</div>
      <div class="lbl">Compliance Health Score</div>
    </div>
    <div class="score-card" style="border-color:#dc3545;">
      <div class="val" style="color:#dc3545">${overdueRows.length}</div>
      <div class="lbl">Overdue</div>
    </div>
    <div class="score-card" style="border-color:#fd7e14;">
      <div class="val" style="color:#b45309">${dueSoonRows.length}</div>
      <div class="lbl">Due Within 30 Days</div>
    </div>
    <div class="score-card" style="border-color:#198754;">
      <div class="val" style="color:#198754">${currentRows.length}</div>
      <div class="lbl">Current</div>
    </div>
    <div class="score-card">
      <div class="val">${totalItems}</div>
      <div class="lbl">Total Items</div>
    </div>
  </div>

  ${overdueRows.length > 0 ? `
  <div class="section-head section-overdue">⚠️ Overdue (${overdueRows.length})</div>
  <table>
    <thead><tr><th>Standard</th><th>Task / Inspection</th><th>Asset / Location</th><th>Frequency</th><th>Last Completed</th><th>Next Due</th><th>Cert Filed</th><th>Cert #</th><th>Inspector</th></tr></thead>
    <tbody>${renderRows(overdueRows, '#fff5f5')}</tbody>
  </table>` : ''}

  ${dueSoonRows.length > 0 ? `
  <div class="section-head section-duesoon">🕔 Due Within 30 Days (${dueSoonRows.length})</div>
  <table>
    <thead><tr><th>Standard</th><th>Task / Inspection</th><th>Asset / Location</th><th>Frequency</th><th>Last Completed</th><th>Next Due</th><th>Cert Filed</th><th>Cert #</th><th>Inspector</th></tr></thead>
    <tbody>${renderRows(dueSoonRows, '#fffdf0')}</tbody>
  </table>` : ''}

  ${currentRows.length > 0 ? `
  <div class="section-head section-current">✅ Current (${currentRows.length})</div>
  <table>
    <thead><tr><th>Standard</th><th>Task / Inspection</th><th>Asset / Location</th><th>Frequency</th><th>Last Completed</th><th>Next Due</th><th>Cert Filed</th><th>Cert #</th><th>Inspector</th></tr></thead>
    <tbody>${renderRows(currentRows, 'white')}</tbody>
  </table>` : ''}

  <div class="footer">
    <span>MEP Tools${orgName ? ' — ' + orgName : ''}</span>
    <span>Confidential — For internal and owner use only</span>
  </div>

</div></body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  const handleTemplateSelect = (idx: string) => {
    if (!idx) { setForm(f => ({ ...f, templateIndex: '' })); return; }
    const t = COMPLIANCE_TEMPLATES[parseInt(idx)];
    setForm(f => ({
      ...f, templateIndex: idx,
      standard: t.standard, taskName: t.taskName,
      frequencyValue: t.frequencyValue, frequencyUnit: t.frequencyUnit,
      assetDescription: t.assetDescription,
    }));
  };

  const save = async () => {
    if (!form.assetDescription || !form.standard || !form.taskName || !form.nextDueDate) {
      setFormError('Asset, standard, task name, and next due date are required.'); return;
    }
    setFormError(null); setSaving(true);
    try {
      await createComplianceRecord({
        projectId,
        assetDescription: form.assetDescription,
        assetLocation: form.assetLocation || undefined,
        standard: form.standard,
        taskName: form.taskName,
        frequencyValue: form.frequencyValue,
        frequencyUnit: form.frequencyUnit,
        nextDueDate: form.nextDueDate,
        lastCompletedDate: form.lastCompletedDate || undefined,
        certificateFiled: form.certificateFiled,
        certificateNumber: form.certificateNumber || undefined,
        inspector: form.inspector || undefined,
        notes: form.notes || undefined,
      });
      setShowForm(false); setForm(emptyForm()); await load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const submitComplete = async (r: ComplianceRecord) => {
    const nextDueDate = addFrequency(completeForm.completedDate, r.frequencyValue, r.frequencyUnit);
    await updateComplianceRecord(r.id, {
      lastCompletedDate: completeForm.completedDate,
      nextDueDate,
      certificateFiled: completeForm.certificateFiled,
      certificateNumber: completeForm.certificateNumber || undefined,
      inspector: completeForm.inspector || undefined,
    });
    setCompletingId(null);
    setCompleteForm({ completedDate: today(), certificateFiled: false, certificateNumber: '', inspector: '' });
    await load();
  };

  const deleteRecord = async (id: string) => {
    await deleteComplianceRecord(id); await load();
  };

  const createWOFromAsset = async (asset: CriticalAsset) => {
    setCreatingWOFor(asset.id);
    try {
      const priorityMap: Record<string, 'P1'|'P2'|'P3'> = { Failed: 'P1', Critical: 'P2', Poor: 'P3' };
      const priority = priorityMap[asset.rating ?? 'Poor'] ?? 'P2';
      await createWorkOrder({
        projectId,
        type: 'reactive',
        priority,
        status: 'open',
        title: `${asset.rating} Asset: ${asset.description || asset.assetId}`,
        description: `Asset rated ${asset.rating} in Facility Assessment (${asset.system} system).\nLocation: ${asset.location || 'Not specified'}\nAsset ID: ${asset.assetId}\nScore: ${asset.computedScore.toFixed(1)}`,
        assetDescription: asset.description || asset.assetId,
        assetLocation: asset.location || undefined,
        trade: asset.system === 'electrical' ? 'Electrician' : asset.system === 'plumbing' ? 'Plumber' : 'HVAC Tech',
      });
      alert(`P${priority.slice(1)} Work Order created for ${asset.description || asset.assetId}. View it in the PM Scheduler → Work Orders tab.`);
    } catch (e: any) { setError(e.message); }
    finally { setCreatingWOFor(null); }
  };

  // Computed
  const overdue = records.filter(r => r.status === 'overdue');
  const dueSoon = records.filter(r => r.status === 'due-soon');
  const current = records.filter(r => r.status === 'current');
  const totalItems = records.length;
  const healthScore = totalItems === 0 ? 100 : Math.round((current.length / totalItems) * 100);

  const grid = buildGrid(calDate.getFullYear(), calDate.getMonth());
  const byDay = records.reduce((acc, r) => {
    if (!acc[r.nextDueDate]) acc[r.nextDueDate] = [];
    acc[r.nextDueDate].push(r); return acc;
  }, {} as Record<string, ComplianceRecord[]>);

  const todayStr = today();

  if (loading) return <Card loading />;

  const formGrid: React.CSSProperties = { display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' };
  const healthColor = healthScore >= 80 ? token.colorSuccess : healthScore >= 50 ? token.colorWarningText : token.colorError;
  const regRowTint = (s: ComplianceRecord['status']): React.CSSProperties =>
    s === 'overdue' ? { background: token.colorErrorBg } : s === 'due-soon' ? { background: token.colorWarningBg } : {};

  const field = (label: string, node: React.ReactNode, span?: boolean) => (
    <div style={span ? { gridColumn: '1 / -1' } : undefined}>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{label}</div>
      {node}
    </div>
  );

  const failedCount = criticalAssets.filter(a => a.rating === 'Failed').length;
  const criticalCount = criticalAssets.filter(a => a.rating === 'Critical').length;
  const poorCount = criticalAssets.filter(a => a.rating === 'Poor').length;

  const renderCompleteForm = (r: ComplianceRecord) => (
    <div style={{ display: 'grid', gap: 10, maxWidth: 560 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {field('Completion Date', <Input type="date" value={completeForm.completedDate} onChange={e => setCompleteForm(f => ({ ...f, completedDate: e.target.value }))} />)}
        {field('Certificate / Report #', <Input value={completeForm.certificateNumber} onChange={e => setCompleteForm(f => ({ ...f, certificateNumber: e.target.value }))} placeholder="Optional" />)}
      </div>
      {field('Inspector', <Input value={completeForm.inspector} onChange={e => setCompleteForm(f => ({ ...f, inspector: e.target.value }))} placeholder="Optional" />)}
      <Checkbox checked={completeForm.certificateFiled} onChange={e => setCompleteForm(f => ({ ...f, certificateFiled: e.target.checked }))}>
        Certificate / report filed with AHJ
      </Checkbox>
      <Space>
        <Button type="primary" size="small" onClick={() => submitComplete(r)}>Save</Button>
        <Button size="small" onClick={() => setCompletingId(null)}>Cancel</Button>
      </Space>
    </div>
  );

  // ── CALENDAR VIEW ──
  const renderCalendarView = () => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <Button icon={<LeftOutlined />} aria-label="Previous month" onClick={() => { setCalDate(new Date(calDate.getFullYear(), calDate.getMonth()-1, 1)); setSelectedDay(null); }} />
        <Typography.Title level={5} style={{ margin: 0 }}>{MONTH_NAMES[calDate.getMonth()]} {calDate.getFullYear()}</Typography.Title>
        <Button icon={<RightOutlined />} aria-label="Next month" onClick={() => { setCalDate(new Date(calDate.getFullYear(), calDate.getMonth()+1, 1)); setSelectedDay(null); }} />
      </div>

      {/* Custom month grid, AntD-token styled */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
        {DOW_LABELS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 12, color: token.colorTextTertiary, fontWeight: 600, padding: '4px 0' }}>{d}</div>
        ))}
        {grid.map((day, i) => {
          if (!day) return <div key={i} style={{ minHeight: 90, background: token.colorFillQuaternary, borderRadius: token.borderRadiusLG }} />;
          const dayRecords = byDay[day] ?? [];
          const isToday = day === todayStr;
          const isSelected = day === selectedDay;
          return (
            <div
              key={day} onClick={() => setSelectedDay(isSelected ? null : day)}
              style={{
                minHeight: 90, borderRadius: token.borderRadiusLG, padding: '5px 6px',
                cursor: dayRecords.length > 0 ? 'pointer' : 'default',
                border: `1px solid ${isSelected ? token.colorPrimary : token.colorBorderSecondary}`,
                background: isSelected ? token.colorPrimaryBg : token.colorBgContainer,
              }}
            >
              <div style={isToday
                ? { background: token.colorPrimary, color: '#fff', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 600 }
                : { fontSize: 11.5, fontWeight: 600, color: token.colorTextTertiary }}
              >
                {parseInt(day.slice(-2), 10)}
              </div>
              {dayRecords.slice(0, 3).map(r => (
                <Tag
                  key={r.id} color={STATUS_COLOR[r.status]} title={`${r.standard} — ${r.assetDescription}`}
                  style={{ display: 'block', fontSize: 10, lineHeight: '16px', paddingInline: 4, marginInlineEnd: 0, marginTop: 3, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {r.standard}
                </Tag>
              ))}
              {dayRecords.length > 3 && <div style={{ fontSize: 10, color: token.colorTextTertiary, marginTop: 2 }}>+{dayRecords.length - 3} more</div>}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 14, marginTop: 10, justifyContent: 'flex-end' }}>
        {[[token.colorError, 'Overdue'], [token.colorWarning, 'Due Within 30 Days'], [token.colorSuccess, 'Current']].map(([color, label]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: color, display: 'inline-block' }} />
            <span style={{ fontSize: 11.5, color: token.colorTextTertiary }}>{label}</span>
          </div>
        ))}
      </div>

      {selectedDay && (byDay[selectedDay] ?? []).length > 0 && (
        <Card
          size="small" style={{ marginTop: 14 }}
          title={parseLocalDate(selectedDay).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          extra={<Button type="text" size="small" icon={<CloseOutlined />} aria-label="Close day detail" onClick={() => setSelectedDay(null)} />}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {(byDay[selectedDay] ?? []).map(r => (
              <div key={r.id}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <Tag color={STATUS_COLOR[r.status]} style={{ marginTop: 2, flexShrink: 0 }}>{STATUS_LABEL[r.status]}</Tag>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>{r.assetDescription}{r.assetLocation ? ` · ${r.assetLocation}` : ''}</div>
                    <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block' }}>{r.taskName}</Typography.Text>
                    <Tag style={{ marginTop: 4 }}>{r.standard}</Tag>
                    {r.lastCompletedDate && <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Last: {r.lastCompletedDate}</Typography.Text>}
                  </div>
                  <Button
                    size="small" icon={<CheckOutlined />} title="Mark Complete" aria-label="Mark complete"
                    onClick={() => { setCompletingId(r.id); setCompleteForm({ completedDate: today(), certificateFiled: false, certificateNumber: '', inspector: '' }); }}
                  />
                </div>
                {completingId === r.id && (
                  <div style={{ marginTop: 12, borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 12 }}>
                    {renderCompleteForm(r)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );

  // ── COMPLIANCE REGISTER ──
  const registerColumns: ColumnsType<ComplianceRecord> = [
    { title: 'Standard', dataIndex: 'standard', width: 150, render: v => <Tag>{v}</Tag> },
    {
      title: 'Asset / Task', key: 'task',
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600 }}>{r.taskName}</div>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>{r.assetDescription}{r.assetLocation ? ` · ${r.assetLocation}` : ''}</Typography.Text>
        </>
      ),
    },
    { title: 'Frequency', key: 'freq', width: 110, render: (_, r) => <span style={{ whiteSpace: 'nowrap' }}>{r.frequencyValue} {r.frequencyUnit}</span> },
    {
      title: 'Last Completed', dataIndex: 'lastCompletedDate', width: 130,
      render: v => v ? <span style={{ whiteSpace: 'nowrap' }}>{v}</span> : <Typography.Text type="secondary">Never</Typography.Text>,
    },
    { title: 'Next Due', dataIndex: 'nextDueDate', width: 120, render: v => <b style={{ whiteSpace: 'nowrap' }}>{v}</b> },
    {
      title: 'Cert', key: 'cert', width: 60, align: 'center',
      render: (_, r) => r.certificateFiled
        ? <CheckCircleFilled style={{ color: token.colorSuccess }} title={r.certificateNumber || 'Filed'} />
        : <CloseCircleOutlined style={{ color: token.colorTextQuaternary }} title="Not filed" />,
    },
    {
      title: 'Status', dataIndex: 'status', width: 110,
      render: (v: ComplianceRecord['status']) => <Tag color={STATUS_COLOR[v]}>{STATUS_LABEL[v]}</Tag>,
    },
    {
      title: '', key: 'actions', width: 90, align: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button
            type="text" size="small" icon={<CheckOutlined />} title="Mark Complete" aria-label="Mark complete"
            onClick={() => { setCompletingId(completingId === r.id ? null : r.id); setCompleteForm({ completedDate: today(), certificateFiled: r.certificateFiled ?? false, certificateNumber: r.certificateNumber ?? '', inspector: r.inspector ?? '' }); }}
          />
          <Popconfirm title="Delete this compliance record?" onConfirm={() => deleteRecord(r.id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} title="Delete" aria-label="Delete record" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderRegisterView = () => (
    <Card styles={{ body: { padding: 0 } }}>
      <Table<ComplianceRecord>
        rowKey="id"
        size="middle"
        columns={registerColumns}
        dataSource={records}
        pagination={false}
        onRow={r => ({ style: regRowTint(r.status) })}
        expandable={{
          expandedRowKeys: completingId ? [completingId] : [],
          expandedRowRender: r => renderCompleteForm(r),
          showExpandColumn: false,
        }}
        locale={{ emptyText: 'No compliance items yet. Add one above.' }}
        scroll={{ x: true }}
      />
    </Card>
  );

  // ── CRITICAL ASSETS ──
  const criticalColumns: ColumnsType<CriticalAsset> = [
    {
      title: 'Rating', dataIndex: 'rating', width: 100,
      render: (v: string | undefined) => <Tag color={RATING_TAG[v ?? ''] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Asset', key: 'asset',
      render: (_, a) => (
        <>
          <div style={{ fontWeight: 600 }}>{a.description || a.assetId}</div>
          {a.assetId && a.description && <Typography.Text type="secondary" style={{ fontSize: 13 }}>ID: {a.assetId}</Typography.Text>}
        </>
      ),
    },
    { title: 'System', dataIndex: 'system', width: 110, render: v => <span style={{ textTransform: 'capitalize' }}>{v}</span> },
    { title: 'Location', dataIndex: 'location', width: 140, render: v => v || <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Score', dataIndex: 'computedScore', width: 80, align: 'right', render: (v: number) => <b>{v.toFixed(1)}</b> },
    { title: 'RSL', dataIndex: 'remainingServiceLife', width: 70, align: 'right', render: v => v ?? <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Assessment', dataIndex: 'assessmentName', width: 150, render: v => <Typography.Text type="secondary">{v || 'Assessment'}</Typography.Text> },
    {
      title: '', key: 'action', width: 130, align: 'right',
      render: (_, a) => (
        <Button danger size="small" icon={<FileAddOutlined />} loading={creatingWOFor === a.id} onClick={() => createWOFromAsset(a)} title="Create reactive Work Order">
          Create WO
        </Button>
      ),
    },
  ];

  const renderCriticalView = () => (
    criticalAssets.length === 0 ? (
      <Card>
        <Empty
          image={<CheckCircleFilled style={{ fontSize: 48, color: token.colorSuccess }} />}
          description={(
            <>
              <Typography.Title level={5} style={{ marginBottom: 4 }}>No critical assets found</Typography.Title>
              <Typography.Text type="secondary">All assets in Facility Assessments for this project are rated Average or better.</Typography.Text>
            </>
          )}
        />
      </Card>
    ) : (
      <>
        <Alert
          type="warning" showIcon style={{ marginBottom: 16 }}
          message={<span><b>{criticalAssets.length} asset{criticalAssets.length > 1 ? 's' : ''} rated Failed, Critical, or Poor</b> in Facility Assessment. Create Work Orders to track corrective action.</span>}
        />
        <Card styles={{ body: { padding: 0 } }}>
          <Table<CriticalAsset>
            rowKey={a => `${a.assessmentId}-${a.id}`}
            size="middle"
            columns={criticalColumns}
            dataSource={criticalAssets}
            pagination={false}
            scroll={{ x: true }}
          />
        </Card>
      </>
    )
  );

  const viewItems = [
    { key: 'calendar', label: <span><CalendarOutlined /> Calendar</span>, children: renderCalendarView() },
    { key: 'register', label: <span><UnorderedListOutlined /> Register</span>, children: renderRegisterView() },
    {
      key: 'critical',
      label: (
        <span>
          <WarningOutlined /> Critical
          {criticalAssets.length > 0 && <Tag color="red" style={{ marginLeft: 8, marginInlineEnd: 0 }}>{criticalAssets.length}</Tag>}
        </span>
      ),
      children: renderCriticalView(),
    },
  ];

  return (
    <div>
      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      {/* Page head */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
        <Typography.Title level={4} style={{ margin: 0 }}><SafetyCertificateOutlined /> Compliance</Typography.Title>
        <Typography.Text type="secondary">Inspection &amp; certification tracking</Typography.Text>
      </Card>

      {/* Critical-asset banner */}
      {criticalAssets.length > 0 && (
        <div onClick={() => setView('critical')} style={{ cursor: 'pointer', marginBottom: 16 }}>
          <Alert
            type="error" showIcon icon={<ExclamationCircleOutlined />}
            message={(
              <b>
                {failedCount > 0 ? `${failedCount} Failed, ` : ''}
                {criticalCount > 0 ? `${criticalCount} Critical, ` : ''}
                {poorCount > 0 ? `${poorCount} Poor ` : ''}
                asset{criticalAssets.length > 1 ? 's' : ''} flagged
              </b>
            )}
            description="From Facility Assessment — view & create work orders →"
          />
        </div>
      )}

      {/* Stat strip */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col flex="1.3 1 0">
          <Card styles={{ body: { padding: '20px 24px' } }}>
            <Statistic title="Compliance health" value={healthScore} suffix="%" valueStyle={{ color: healthColor }} />
            <Progress percent={healthScore} showInfo={false} strokeColor={healthColor} size="small" style={{ margin: '6px 0 2px' }} />
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>{current.length}/{totalItems} current</Typography.Text>
          </Card>
        </Col>
        <Col flex="1 1 0">
          <Card hoverable onClick={() => setView('register')} styles={{ body: { padding: '20px 24px' } }}>
            <Statistic title="Overdue →" value={overdue.length} valueStyle={{ color: token.colorError }} prefix={<ExclamationCircleOutlined />} />
          </Card>
        </Col>
        <Col flex="1 1 0">
          <Card hoverable onClick={() => setView('register')} styles={{ body: { padding: '20px 24px' } }}>
            <Statistic title="Due Within 30 Days →" value={dueSoon.length} valueStyle={{ color: token.colorWarningText }} prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col flex="1 1 0">
          <Card styles={{ body: { padding: '20px 24px' } }}>
            <Statistic title={`Current · ${totalItems} total`} value={current.length} valueStyle={{ color: token.colorSuccess }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
      </Row>

      {overdue.length > 0 && (
        <Alert
          type="error" showIcon style={{ marginBottom: 16 }}
          message={(
            <span>
              <b>{overdue.length} compliance item{overdue.length > 1 ? 's' : ''} overdue.</b>{' '}
              {overdue.slice(0, 2).map(r => `${r.standard} — ${r.assetDescription}`).join('; ')}{overdue.length > 2 && ` and ${overdue.length - 2} more.`}
            </span>
          )}
        />
      )}

      {syncResult && (
        <Alert
          type={syncResult.startsWith('✅') ? 'success' : 'info'} showIcon closable
          message={syncResult.replace('✅ ', '')} onClose={() => setSyncResult(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Add form */}
      {showForm && (
        <Card title="New compliance item" style={{ marginBottom: 16 }}>
          <div style={formGrid}>
            {field('Quick-add from common standards', (
              <Select
                style={{ width: '100%' }} allowClear
                placeholder="— Select a template or fill in manually below —"
                value={form.templateIndex || undefined}
                options={COMPLIANCE_TEMPLATES.map((t, i) => ({ value: String(i), label: `${t.standard} — ${t.taskName}` }))}
                onChange={v => handleTemplateSelect(v ?? '')}
              />
            ), true)}
            {field('Asset / Equipment *', <Input value={form.assetDescription} onChange={e => setForm(f => ({ ...f, assetDescription: e.target.value }))} placeholder="e.g. Fire Sprinkler System" />)}
            {field('Location', <Input value={form.assetLocation} onChange={e => setForm(f => ({ ...f, assetLocation: e.target.value }))} placeholder="e.g. Building-wide" />)}
            {field('Compliance Standard *', <Input value={form.standard} onChange={e => setForm(f => ({ ...f, standard: e.target.value }))} placeholder="e.g. NFPA 25" />)}
            {field('Task / Inspection Name *', <Input value={form.taskName} onChange={e => setForm(f => ({ ...f, taskName: e.target.value }))} placeholder="e.g. Quarterly Sprinkler Inspection" />)}
            {field('Frequency', <Input type="number" min={1} value={form.frequencyValue} onChange={e => setForm(f => ({ ...f, frequencyValue: parseInt(e.target.value) || 1 }))} />)}
            {field('Unit', (
              <Select
                style={{ width: '100%' }} value={form.frequencyUnit}
                options={[
                  { value: 'days', label: 'Days' }, { value: 'weeks', label: 'Weeks' },
                  { value: 'months', label: 'Months' }, { value: 'years', label: 'Years' },
                ]}
                onChange={v => setForm(f => ({ ...f, frequencyUnit: v as ComplianceRecord['frequencyUnit'] }))}
              />
            ))}
            {field('Next Due Date *', <Input type="date" value={form.nextDueDate} onChange={e => setForm(f => ({ ...f, nextDueDate: e.target.value }))} />)}
            {field('Last Completed', <Input type="date" value={form.lastCompletedDate} onChange={e => setForm(f => ({ ...f, lastCompletedDate: e.target.value }))} />)}
            {field('Notes', <Input.TextArea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />, true)}
          </div>
          {formError && <Alert type="error" showIcon message={formError} style={{ marginTop: 14 }} />}
          <Space style={{ marginTop: 16 }}>
            <Button type="primary" onClick={save} loading={saving}>Save</Button>
            <Button onClick={() => { setShowForm(false); setForm(emptyForm()); setFormError(null); }}>Cancel</Button>
          </Space>
        </Card>
      )}

      <Tabs
        activeKey={view}
        onChange={k => setView(k as View)}
        items={viewItems}
        tabBarExtraContent={(
          <Space>
            <Button icon={<SyncOutlined />} loading={syncing} onClick={syncFromPMSchedules} title="Import compliance items from PM Schedules">
              Sync from PM
            </Button>
            <Button icon={<PrinterOutlined />} onClick={printReport} title="Print compliance report">Print Report</Button>
            <Button type="primary" icon={showForm ? <CloseOutlined /> : <PlusOutlined />} onClick={() => setShowForm(v => !v)}>
              {showForm ? 'Cancel' : 'Add Item'}
            </Button>
          </Space>
        )}
      />
    </div>
  );
};

export default ComplianceCalendar;
